// Derive human axis titles ("what does X / Y represent") from the bound fields,
// with optional user overrides from properties. Titles only apply to cartesian
// charts (those with an x axis); part-to-whole/flow charts return none.
import type { AxisConfig } from './types';

function humanize(field?: string): string {
  if (!field) return '';
  const leaf = field.replace('[].', '.').split('.').pop() || field;
  return leaf.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).trim();
}

const AGG_PREFIX: Record<string, string> = {
  sum: 'Sum of', avg: 'Avg', min: 'Min', max: 'Max', median: 'Median',
  std_dev: 'Std dev of', count: 'Count of', distinct_count: 'Distinct',
  running_total: 'Running total of', pct_of_total: '% of total',
};
const GRANULARITY = new Set(['year', 'quarter', 'month', 'week', 'day', 'hour', 'minute', 'second']);

function labelFor(cfg?: AxisConfig): string {
  if (!cfg?.field) return '';
  const base = humanize(cfg.field);
  const agg = cfg.aggregation;
  if (!agg || agg === 'actual' || agg === 'dimension' || agg === 'geo') return base;
  if (GRANULARITY.has(agg)) return `${base} (${agg.charAt(0).toUpperCase()}${agg.slice(1)})`;
  const pre = AGG_PREFIX[agg];
  return pre ? `${pre} ${base}` : base;
}

export interface AxisLabels { xLabel: string; yLabel: string }

// `axes` is keyed by canonical axis keys (x/y/value/category/...). We show titles
// only when there's an x axis (cartesian). Overrides win when non-empty.
export function deriveAxisLabels(
  axes: Record<string, AxisConfig> | undefined,
  properties: Record<string, unknown> | undefined,
): AxisLabels {
  const a = axes || {};
  if (!('x' in a)) return { xLabel: '', yLabel: '' };
  const xOverride = String(properties?.xAxisLabel || '').trim();
  const yOverride = String(properties?.yAxisLabel || '').trim();
  return {
    xLabel: xOverride || labelFor(a.x),
    yLabel: yOverride || labelFor(a.y),
  };
}
